import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import {
	AppBar,
	Box,
	Button,
	Card,
	CardContent,
	CardMedia,
	Container,
	Divider,
	IconButton,
	Toolbar,
	Typography,
	useTheme,
	useMediaQuery,
	CircularProgress,
	Grid,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import MovieFilterIcon from "@mui/icons-material/MovieFilter";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import MusicNoteIcon from "@mui/icons-material/MusicNote";

// Note: You'll need to import your images
// For this example, we'll assume the import paths remain the same
import ideaToVideoImage from "../images/ideaToVideo.jpeg";
import img1 from "../images/img1.png";
import img2 from "../images/img2.png";
import img3 from "../images/img3.png";
import img4 from "../images/img4.png";
import img5 from "../images/img5.png";
import img6 from "../images/img6.png";
import { AutoAwesome } from "@mui/icons-material";

function FrontPage() {
	const navigate = useNavigate();
	const auth = useAuth();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	const handleLoginClick = () => {
		auth.signinRedirect();
	};

	if (auth.isLoading) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="100vh"
			>
				<CircularProgress />
			</Box>
		);
	}

	if (auth.error) {
		return (
			<Container>
				<Box my={4} textAlign="center">
					<Typography variant="h4" color="error" gutterBottom>
						Authentication Error
					</Typography>
					<Typography variant="body1">
						{auth.error.message}
					</Typography>
				</Box>
			</Container>
		);
	}

	if (auth.isAuthenticated) {
		localStorage.setItem("auth_user", JSON.stringify(auth.user));
		navigate("/landing");
		return (
			<Container>
				<Box my={4}>
					<Typography variant="h6">
						Hello: {auth.user?.profile.email}
					</Typography>
					<Typography variant="body2" sx={{ mt: 2 }}>
						ID Token: {auth.user?.id_token}
					</Typography>
					<Typography variant="body2">
						Access Token: {auth.user?.access_token}
					</Typography>
					<Typography variant="body2">
						Refresh Token: {auth.user?.refresh_token}
					</Typography>
					<Button
						variant="contained"
						color="primary"
						sx={{ mt: 2 }}
						onClick={() => auth.removeUser()}
					>
						Sign out
					</Button>
				</Box>
			</Container>
		);
	}

	// Features data
	const featuresData = [
		{
			title: "As Easy As 1-2-3",
			description:
				"Provide topic or script, click generate video, sit back and get your video.",
			image: img1,
		},
		{
			title: "High Quality Videos",
			description:
				"Professional grade visual, audio and caption quality.",
			image: img2,
		},
		{
			title: "Human Review",
			description:
				"Option for human review and editing if you ask for it.",
			image: img3,
		},
	];

	// Pricing data
	const pricingData = [
		{
			title: "Free Plan",
			description:
				"Our main goal is to flourish your creativity. Free plan helps you to explore your ideas.",
			image: img4,
		},
		{
			title: "Pay As You Go",
			description:
				"We don't want you to pay for our services even when you're not using it. Thats why we offer a super transparent pay as you go plan.",
			image: img5,
		},
		{
			title: "Monthly Subscription",
			description:
				"If you believe that you are a heavy user and would like some additional discount or service, you can choose our monthly subscription.",
			image: img6,
		},
	];

	return (
		<Box sx={{ flexGrow: 1 }}>
			{/* Header/AppBar */}
			<AppBar
				position="sticky"
				elevation={0}
				color="default"
				sx={{ backgroundColor: "white" }}
			>
				<Toolbar>
					<Typography
						variant="h6"
						component="div"
						sx={{
							flexGrow: 1,
							color: theme.palette.primary.main,
							fontWeight: 700,
							display: "flex",
							alignItems: "center",
						}}
					>
						<AutoAwesome sx={{ mr: 1 }} />
						MagicVerse: Your Story, Your Way
					</Typography>

					{isMobile ? (
						<IconButton
							edge="end"
							color="inherit"
							aria-label="menu"
						>
							<MenuIcon />
						</IconButton>
					) : (
						<Box sx={{ display: "flex", alignItems: "center" }}>
							<Button color="inherit" href="#features">
								Features
							</Button>
							<Button color="inherit" href="#pricing">
								Pricing
							</Button>
							<Button color="inherit" href="#features">
								FAQ
							</Button>
							<Button
								variant="contained"
								color="primary"
								onClick={handleLoginClick}
								sx={{ ml: 2 }}
							>
								Login
							</Button>
						</Box>
					)}
				</Toolbar>
			</AppBar>

			{/* Hero Section */}
			{/* <Box
				sx={{
					backgroundColor: theme.palette.background.default,
					pt: 8,
					pb: 6,
				}}
			>
				<Container maxWidth="lg">
					<Grid container spacing={4} alignItems="center">
						<Grid item xs={12} md={6}>
							<Typography
								component="h1"
								variant="h2"
								color="text.primary"
								gutterBottom
								sx={{ fontWeight: 700 }}
							>
								Transform Your Ideas Into Stunning Videos
							</Typography>
							<Typography
								variant="h5"
								color="text.secondary"
								paragraph
							>
								Leverage AI to create professional-grade videos
								in minutes. No prior experience required! Just
								provide a topic or script and get your video.
							</Typography>
							<Box sx={{ mt: 4 }}>
								<Button
									variant="contained"
									size="large"
									onClick={handleLoginClick}
									sx={{ mr: 2, mb: isMobile ? 2 : 0 }}
								>
									Get Started
								</Button>
								<Button
									variant="outlined"
									size="large"
									href="#footer"
								>
									Find Us on Social Media
								</Button>
							</Box>
						</Grid>
						<Grid item xs={12} md={6}>
							<Box
								component="img"
								src={ideaToVideoImage}
								alt="Video Creation"
								sx={{
									width: "100%",
									height: "auto",
									maxHeight: 400,
									borderRadius: 2,
									boxShadow: 3,
								}}
							/>
						</Grid>
					</Grid>
				</Container>
			</Box> */}

			<Container maxWidth="lg">
				<Box
					sx={{
						backgroundColor: theme.palette.background.default,
						pt: 8,
						pb: 6,
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
						gap: 4,
						alignItems: "center",
						// px: { xs: 2, sm: 4, md: 8 },
					}}
					// className="max-w-6xl mx-auto"
				>
					<Box>
						<Typography
							component="h1"
							variant="h2"
							color="text.primary"
							gutterBottom
							sx={{ fontWeight: 700 }}
						>
							Transform Your Ideas Into Stunning Videos
						</Typography>
						<Typography
							variant="h5"
							color="text.secondary"
							paragraph
						>
							Leverage AI to create professional-grade videos in
							minutes. No prior experience required! Just provide
							a topic or script and get your video.
						</Typography>
						<Box
							sx={{ mt: 4 }}
							className="flex flex-row items-center gap-2"
						>
							<Button
								variant="contained"
								size="large"
								onClick={handleLoginClick}
								// sx={{ mr: 2, mb: isMobile ? 2 : 0 }}
							>
								Get Started
							</Button>
							<Button
								variant="outlined"
								size="large"
								href="#footer"
							>
								Find Us on Social Media
							</Button>
						</Box>
					</Box>
					<Box>
						<Box
							component="img"
							src={ideaToVideoImage}
							alt="Video Creation"
							sx={{
								width: "100%",
								height: "auto",
								maxHeight: 400,
								borderRadius: 2,
								boxShadow: 3,
							}}
						/>
					</Box>
				</Box>
			</Container>

			{/* Features Section */}
			<Box id="features" sx={{ py: 8, backgroundColor: "#f5f5f5" }}>
				<Container maxWidth="lg">
					<Typography
						component="h2"
						variant="h3"
						align="center"
						color="text.primary"
						gutterBottom
						sx={{ mb: 6, fontWeight: 600 }}
					>
						Why MagicVerse?
					</Typography>

					{/* CSS Grid layout for features */}
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "1fr",
								sm: "repeat(2, 1fr)",
								md: "repeat(3, 1fr)",
							},
							gap: 4,
						}}
					>
						{featuresData.map((feature, index) => (
							<Card
								key={index}
								elevation={2}
								sx={{
									height: "100%",
									display: "flex",
									flexDirection: "column",
									transition:
										"transform 0.2s, box-shadow 0.2s",
									"&:hover": {
										transform: "translateY(-5px)",
										boxShadow: 6,
									},
								}}
							>
								<Box
									sx={{
										p: 2,
										display: "flex",
										justifyContent: "center",
									}}
								>
									<CardMedia
										component="img"
										sx={{ width: 100, height: 100 }}
										image={feature.image}
										alt={feature.title}
									/>
								</Box>
								<CardContent sx={{ flexGrow: 1 }}>
									<Typography
										gutterBottom
										variant="h5"
										component="h3"
										align="center"
									>
										{feature.title}
									</Typography>
									<Typography align="center">
										{feature.description}
									</Typography>
								</CardContent>
							</Card>
						))}
					</Box>
				</Container>
			</Box>

			{/* Pricing Section */}
			<Box id="pricing" sx={{ py: 8 }}>
				<Container maxWidth="lg">
					<Typography
						component="h2"
						variant="h3"
						align="center"
						color="text.primary"
						gutterBottom
						sx={{ mb: 6, fontWeight: 600 }}
					>
						Pricing
					</Typography>

					{/* CSS Grid layout for pricing */}
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "1fr",
								sm: "repeat(2, 1fr)",
								md: "repeat(3, 1fr)",
							},
							gap: 4,
						}}
					>
						{pricingData.map((plan, index) => (
							<Card
								key={index}
								elevation={2}
								sx={{
									height: "100%",
									display: "flex",
									flexDirection: "column",
									transition:
										"transform 0.2s, box-shadow 0.2s",
									"&:hover": {
										transform: "translateY(-5px)",
										boxShadow: 6,
									},
								}}
							>
								<Box
									sx={{
										p: 2,
										display: "flex",
										justifyContent: "center",
									}}
								>
									<CardMedia
										component="img"
										sx={{ width: 100, height: 100 }}
										image={plan.image}
										alt={plan.title}
									/>
								</Box>
								<CardContent sx={{ flexGrow: 1 }}>
									<Typography
										gutterBottom
										variant="h5"
										component="h3"
										align="center"
									>
										{plan.title}
									</Typography>
									<Typography align="center">
										{plan.description}
									</Typography>
								</CardContent>
							</Card>
						))}
					</Box>
				</Container>
			</Box>

			{/* CTA Section */}
			<Box
				sx={{
					bgcolor: theme.palette.primary.main,
					pt: 8,
					pb: 8,
					color: "white",
					textAlign: "center",
				}}
			>
				<Container maxWidth="sm">
					<Typography variant="h4" component="h2" gutterBottom>
						Ready to Create Your First Video?
					</Typography>
					<Button
						variant="contained"
						size="large"
						onClick={handleLoginClick}
						sx={{
							mt: 3,
							backgroundColor: "white",
							color: theme.palette.primary.main,
							"&:hover": {
								backgroundColor: "#f0f0f0",
							},
						}}
					>
						Start Now
					</Button>
				</Container>
			</Box>

			{/* Footer */}
			<Box id="footer" sx={{ bgcolor: "background.paper", p: 6 }}>
				<Container maxWidth="lg">
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
							gap: 4,
						}}
					>
						<Box>
							<Typography
								variant="h6"
								color="text.primary"
								gutterBottom
							>
								MagicVerse
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Transform your ideas into stunning videos with
								AI
							</Typography>
						</Box>
						<Box>
							<Typography
								variant="h6"
								color="text.primary"
								gutterBottom
								className="text-right"
							>
								Connect With Us
							</Typography>
							<Box
								sx={{ display: "flex", gap: 2 }}
								className="justify-end"
							>
								<IconButton
									component="a"
									href="https://www.tiktok.com/@magicversestories"
									target="_blank"
									aria-label="TikTok"
								>
									<MusicNoteIcon />
								</IconButton>
								<IconButton
									component="a"
									href="https://www.youtube.com/@MagicVerseStories"
									target="_blank"
									aria-label="YouTube"
								>
									<YouTubeIcon />
								</IconButton>
								<IconButton
									component="a"
									href="https://www.instagram.com/magicversestories/"
									target="_blank"
									aria-label="Instagram"
								>
									<InstagramIcon />
								</IconButton>
							</Box>
						</Box>
					</Box>
					<Divider sx={{ mt: 4, mb: 4 }} />
					<Typography
						variant="body2"
						color="text.secondary"
						align="center"
					>
						© 2024 MagicVerse. All Rights Reserved.
					</Typography>
				</Container>
			</Box>
		</Box>
	);
}

export default FrontPage;
